import { useMutation, useQueryClient } from "@tanstack/react-query";
import _ from "underscore";
import {
  callFunctionIfExists,
  getErrorMessage,
  groupTagData,
} from "../../../utils/GeneralUtils";
import TenantAPI from "../../api/TenantAPI";

/**
 * useUpdateTag - Hook to update a tag. Uses react-query to cache results. Flexible for all scopes
 */
const useUpdateTag = () => {
  const queryClient = useQueryClient();

  const updateTags = (scope: any, newTag: any) => {
    const newTags = queryClient.getQueryData<any>(["tags", scope])?.tags;

    const tagIndex = _.findIndex(newTags, { id: newTag.id });

    if (tagIndex > -1) {
      newTags[tagIndex] = newTag;
    } else {
      return console.error("Tag not found in useUpdateTag");
    }

    queryClient.setQueryData(["tags", scope], {
      tags: newTags,
      tagGroups: groupTagData(newTags),
    });
  };

  const updateTag = useMutation({
    mutationFn: ({
      scope,
      payload,
      tagID,
    }: {
      scope: any;
      payload: any;
      tagID: string;
      onSuccess?: () => void;
    }) => {
      payload.Scope = scope;

      if (scope.Type === "Cloud") {
        return TenantAPI.updateCloudTag(tagID, payload).then((data) =>
          updateTags(scope, data.data.data)
        );
      }

      if (scope.Type === "Workspace") {
        return TenantAPI.updateWorkspaceTag(
          scope?.WorkspaceID,
          tagID,
          payload
        ).then((data) => updateTags(scope, data.data.data));
      }

      if (scope.Type === "Module") {
        return TenantAPI.updateModuleTag(
          scope?.ModuleAPIName,
          tagID,
          payload
        ).then((data) => updateTags(scope, data.data.data));
      }

      if (scope.Type === "Person") {
        return TenantAPI.updateUserTag(scope?.PersonID, tagID, payload).then(
          (data) => updateTags(scope, data.data.data)
        );
      }

      return Promise.reject("Invalid Scope Provided to useCreateTag");
    },

    onSuccess: (_, { onSuccess }) => {
      callFunctionIfExists(onSuccess);
    },
  });

  return {
    updateTag: updateTag.mutate,
    updatingTag: updateTag.isPending,
    updateError: updateTag.error ? getErrorMessage(updateTag.error) : null,
  };
};

export default useUpdateTag;
